import styled from "styled-components";
import { ReactComponent as CameraIcon } from "../../assets/camera.svg";
import { useState } from "react";
import { PHOTO_LIMIT } from "../../config";
import { Step } from "../InspectionForm";
import type { Image } from "../../hooks/useImageUploader";
import Resizer from "react-image-file-resizer";

interface ImageInputButtonProps {
  step: Step;
  images: Image[];
  onAdd: (files: FileList) => void;
  photoLimit?: number;
}

const ImageInputButton = ({
  step,
  images,
  onAdd,
  photoLimit,
}: ImageInputButtonProps) => {
  const [error, setError] = useState<string>();
  const limit = photoLimit ?? PHOTO_LIMIT;

  const resizeImages = async (files: FileList) => {
    const resizePromises = Array.from(files).map(
      (file) =>
        new Promise<File>((resolve, reject) => {
          try {
            Resizer.imageFileResizer(
              file,
              1400, // max width
              1400, // max height
              "JPEG", // output format
              80, // quality
              0, // rotation
              (value: any) => {
                try {
                  const resizedFile = new File([value], file.name, {
                    type: "image/jpeg",
                  });
                  resolve(resizedFile);
                } catch (err) {
                  console.error("Error creating resized file:", err);
                  reject(err);
                }
              },
              "blob"
            );
          } catch (err) {
            console.error("Error resizing image:", err);
            reject(err);
          }
        })
    );
    try {
      const results = await Promise.all(resizePromises);
      return results;
    } catch (err) {
      // Optionally handle errors globally here
      setError(
        "Er is een fout opgetreden bij het verkleinen van de afbeelding."
      );
      return [];
    }
  };

  return (
    <>
      {error ? <ErrorText>{error}</ErrorText> : null}
      <ImageAddButton>
        <CameraIcon fill="#0075BC" />
        <ImageButtonText>Selecteer foto's</ImageButtonText>
        <input
          type="file"
          accept="image/png, image/jpeg, image/jpg"
          multiple
          style={{ display: "none" }}
          onChange={async ({ target }) => {
            if (!target?.files?.length) return;
            if (target?.files?.length + images?.length > limit) {
              setError(
                `Je kan ${limit} foto${
                  limit > 1 ? "'s" : ""
                } per stap toevoegen`
              );
              return;
            }
            const resized = await resizeImages(target.files);
            // Convert array to FileList-like object for onAdd
            const dataTransfer = new DataTransfer();
            resized.forEach((file) => dataTransfer.items.add(file));
            onAdd(dataTransfer.files);
          }}
        />
      </ImageAddButton>
    </>
  );
};

export default ImageInputButton;

const ImageAddButton = styled.label`
  cursor: pointer;
  width: 190px;
  height: 139px;

  background: #ffffff;

  /* Dropshadow */
  box-shadow: 0px 10px 15px rgba(168, 168, 168, 0.2);
  border-radius: 5px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.01);
  }
`;

const ImageButtonText = styled.span`
  padding-top: 24px;
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 120%;

  /* or 19px */
  display: flex;
  align-items: center;
  text-align: center;

  /* BoerenNatuur Blauw */
  color: #0075bc;
`;

const ErrorText = styled.div`
  /* Text small */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 120%;

  padding-top: 5px;
  padding-bottom: 5px;
`;
